import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { AuditLogOptionsController_scopes } from '@/services/services';

import { mapAuditScopeOptions } from './mappers';
import type { UseAuditScopeSearchResult } from './types';

const AUDIT_SCOPE_SEARCH_KEY = 'auditLogScopes';

/** The backend's bounds for `q`: shorter matches too much, longer is refused. */
export const AUDIT_SCOPE_QUERY_MIN = 2;
export const AUDIT_SCOPE_QUERY_MAX = 100;

const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_STALE_MS = 5 * 60 * 1000;

function isSearchable(query: string): boolean {
    return query.length >= AUDIT_SCOPE_QUERY_MIN && query.length <= AUDIT_SCOPE_QUERY_MAX;
}

/**
 * Clients, sites and pens whose current name contains `query`, archived and deleted ones
 * included, since the log still refers to them. Matches from an older query are never
 * shown while a newer one waits out the debounce.
 */
export function useAuditScopeSearch(query: string): UseAuditScopeSearchResult {
    const trimmed = query.trim();
    const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
    const settled = debounced === trimmed;
    const searchable = isSearchable(trimmed);

    const search = useQuery({
        queryKey: [AUDIT_SCOPE_SEARCH_KEY, debounced],
        queryFn: async () => (await AuditLogOptionsController_scopes({ q: debounced })).data,
        enabled: settled && isSearchable(debounced),
        staleTime: SEARCH_STALE_MS,
        refetchOnWindowFocus: false,
    });

    const options = useMemo(
        () => (settled && searchable ? mapAuditScopeOptions(search.data) : []),
        [settled, searchable, search.data]
    );

    return {
        options,
        isFetching: searchable && (!settled || search.isFetching),
        isSearchable: searchable,
        error: search.error,
    };
}
